import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  styled,
  Divider,
  Avatar,
  TableCell,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Checkbox,
} from "@mui/material";
import { useParams } from "react-router-dom";
import blogPosts from "../../data/blogPosts.json";
import useSEO from "../../hooks/BlogUseSeo.js";
import { generateBlogSEO, generateFAQSchema } from "../../hooks/BlogSeoUtils.js";
import modelsData from "../../subComponents/LatestModelsandTheirPerformance/ModelsComparisonData.js";
import ModelComparison from "../../subComponents/LatestModelsandTheirPerformance/LatestModelsandTheirPerformance.js";
import ZoomableImage from "./components/zoomableImage.js";

const TOCItem = styled(ListItem)(({ theme, active }) => ({
  borderLeft: active
    ? `3px solid ${theme.palette.primary.main}`
    : "3px solid transparent",
  paddingLeft: theme.spacing(2),
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

// ---------- NEW: Safe splitter that ignores delimiters inside [link: ... ] ----------
// Simpler and more reliable approach
// Fixed safeSplitTopLevel function that properly handles [link:url|text] syntax
// ---------- NEW: Interactive Checkbox Component ----------
const InteractiveCheckbox = () => {
  const [checked, setChecked] = useState(false);
  return (
    <Checkbox
      checked={checked}
      onChange={(e) => setChecked(e.target.checked)}
      sx={{
        transform: "scale(1.3)",
        mr: 1,
        p: 0,
        verticalAlign: "bottom",
        color: "#ffffff",
        "&.Mui-checked": {
          color: "primary.main",
        },
      }}
    />
  );
};

// ---------- NEW: Safe splitter that ignores delimiters inside ANY [ ... ] tag ----------
const safeSplitTopLevel = (input, delimiterChar) => {
  if (!input) return [];

  const out = [];
  let cur = "";
  let bracketDepth = 0;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];

    // Track bracket depth for ANY [ ... ] block (links, bold, images, etc.)
    if (ch === "[") {
      bracketDepth++;
      cur += ch;
      continue;
    }
    if (ch === "]" && bracketDepth > 0) {
      bracketDepth--;
      cur += ch;
      continue;
    }

    // Split only when we see the delimiter at top level (not inside brackets)
    if (ch === delimiterChar && bracketDepth === 0) {
      if (cur.trim()) out.push(cur.trim());
      cur = "";
      continue;
    }

    cur += ch;
  }

  if (cur.trim()) out.push(cur.trim());
  return out;
};

// ---------- NEW: find the matching closing bracket for a block, respecting nested [ ... ] ----------
const findBalancedClosing = (str, openIndex) => {
  if (openIndex >= str.length || str[openIndex] !== "[") {
    return -1;
  }

  let depth = 0;
  for (let i = openIndex; i < str.length; i++) {
    const ch = str[i];
    if (ch === "[") {
      depth++;
    } else if (ch === "]") {
      depth--;
      if (depth === 0) {
        return i; // this ] matches the [ at openIndex
      }
    }
  }
  return -1; // not found
};

// CSV-like splitter that ignores commas inside [ ... ]
// Enhanced CSV-like splitter that handles quoted content and ignores commas inside [ ... ]
// CSV-like splitter that handles quotes and ignores commas inside [ ... ]
// CSV-like splitter that handles quotes and ignores commas inside [ ... ]
const safeSplitCSVTopLevel = (input) => {
  if (!input) return [];

  const out = [];
  let cur = "";
  let bracketDepth = 0;
  let inQuotes = false;
  let quoteChar = null;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];

    // Handle quotes
    if ((ch === '"' || ch === "'") && bracketDepth === 0) {
      if (!inQuotes) {
        inQuotes = true;
        quoteChar = ch;
        // Don't include the opening quote in the content
        continue;
      } else if (ch === quoteChar) {
        inQuotes = false;
        quoteChar = null;
        // Don't include the closing quote in the content
        continue;
      }
    }

    // Track bracket depth for ANY [ ... ] block (links, bold, images, etc.)
    if (ch === "[" && !inQuotes) {
      bracketDepth++;
      cur += ch;
      continue;
    }
    if (ch === "]" && bracketDepth > 0 && !inQuotes) {
      bracketDepth--;
      cur += ch;
      continue;
    }

    // Split only when we see comma at top level (not inside brackets or quotes)
    if (ch === "," && bracketDepth === 0 && !inQuotes) {
      if (cur.trim()) out.push(cur.trim());
      cur = "";
      continue;
    }

    cur += ch;
  }

  if (cur.trim()) out.push(cur.trim());
  return out;
};

// Simple Comparison Table Component (like the image)
// Enhanced Simple Comparison Table Component
const SimpleComparisonTable = ({ leftItems, rightItems }) => {
  return (
    <Box
      sx={{
        background: "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(12px)",
        borderRadius: 2,
        overflow: "hidden",
        mt: 2,
        mb: 4,
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
      }}
    >
      {/* Two Column Layout */}
      <Box sx={{ display: "flex" }}>
        {/* Left Column - Chat AI Agents */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            borderRight: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              textAlign: "center",
              color: "white",
              fontSize: "1rem",
              borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
              pb: 2,
            }}
          >
            Chat AI Agents
          </Typography>
          <Box component="ul" sx={{ pl: 0, m: 0, listStyle: "none" }}>
            {leftItems.map((item, index) => (
              <Box
                component="li"
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 2.5,
                  "&:last-child": { mb: 0 },
                }}
              >
                <Box
                  sx={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    mt: "8px",
                    mr: 2,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.6,
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "0.875rem",
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right Column - Voice AI Agents */}
        <Box sx={{ flex: 1, p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              textAlign: "center",
              color: "white",
              fontSize: "1rem",
              borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
              pb: 2,
            }}
          >
            Voice AI Agents
          </Typography>
          <Box component="ul" sx={{ pl: 0, m: 0, listStyle: "none" }}>
            {rightItems.map((item, index) => (
              <Box
                component="li"
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 2.5,
                  "&:last-child": { mb: 0 },
                }}
              >
                <Box
                  sx={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    mt: "8px",
                    mr: 2,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.6,
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "0.875rem",
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
// ---------- UPDATED: Helper to find next special tag ----------
const findNextSpecialTagIndex = (content, startIndex) => {
  const tagTypes = [
    "[img:",
    "[ul:",
    "[ol:",
    "[table:",
    "[h3:",
    "[quote:",
    "[zoomimg:",
    "[code:",
    "[audio:",
    "[simpletable:",
  ];

  let min = Infinity;
  for (const tag of tagTypes) {
    const idx = content.indexOf(tag, startIndex);
    if (idx >= 0 && idx < min) {
      min = idx;
    }
  }
  return min === Infinity ? content.length : min;
};

// Enhanced code block renderer with syntax highlighting
export const renderCodeBlock = (code, language = "text") => {
  return (
    <Box
      sx={{
        my: 3,
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "#1a1a1a",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
      }}
    >
      {/* Header with language & copy */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          backgroundColor: "#2a2a2a",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "#888",
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          {language}
        </Typography>
        <Box
          component="button"
          onClick={() => navigator.clipboard.writeText(code)}
          sx={{
            background: "none",
            border: "none",
            color: "#888",
            cursor: "pointer",
            padding: "4px 8px",
            borderRadius: 1,
            fontSize: "12px",
            transition: "all 0.2s ease",
            "&:hover": {
              color: "#fff",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          Copy
        </Box>
      </Box>

      {/* Code content */}
      <Box
        sx={{
          p: 3,
          overflow: "auto",
          maxHeight: "600px",
          backgroundColor: "#1a1a1a",
        }}
      >
        <Typography
          component="pre"
          sx={{
            fontFamily:
              '"Fira Code", "JetBrains Mono", "Consolas", "Monaco", monospace',
            fontSize: "14px",
            lineHeight: 1.6,
            color: "#e6e6e6",
            m: 0,
            whiteSpace: "pre",
            wordWrap: "break-word",
            overflowWrap: "break-word",
          }}
        >
          <code style={{ color: "#e6e6e6" }}>{code}</code>
        </Typography>
      </Box>
    </Box>
  );
};

// ContentRenderer component to handle different content types
export const ContentRenderer = ({
  content,
  h3Variant = "letters",
  h3Start = 1,
  listTight = true, // NEW
}) => {
  if (!content) return null;

  try {
    return renderContent(content, { h3Variant, h3Start, listTight });
  } catch (error) {
    console.error("Error rendering content:", error);
    return (
      <Typography
        variant="body1"
        sx={{
          lineHeight: 1.7,
          whiteSpace: "pre-line",
        }}
      >
        {content}
      </Typography>
    );
  }
};

// Converts ```lang\n...``` fences into [code:lang|...]
export const normalizeCodeFences = (input) => {
  if (!input) return input;

  // Matches triple backtick fences (with optional language)
  //   group 1: language (letters, numbers, _ + -)
  //   group 2: code body (non-greedy)
  const fenceRegex = /```([a-zA-Z0-9_+\-]*)\n([\s\S]*?)```/g;

  // Escape ']' so our [code: ... ] tag stays balanced during parsing
  const escapeForBracketedTag = (code) => code.replaceAll("]", "&#93;");

  return input.replace(fenceRegex, (_, lang, body) => {
    const language = (lang || "text").trim();
    const safeBody = escapeForBracketedTag((body || "").trim());
    return `[code:${language}|${safeBody}]`;
  });
};

export const unescapeFromBracketedTag = (code) =>
  (code || "").replaceAll("&#93;", "]");

// =======================================
// 3) (Optional) Markdown → [h3:] heading
// =======================================
export const processMarkdownHeadings = (content) => {
  return (content || "").replace(/^## (.+)$/gm, "[h3:$1]");
};

// Extracts [zoomimg:src|caption] occurrences from text and returns clean text + images
const extractInlineZoomimgs = (text) => {
  if (!text) return { clean: text, images: [] };

  const images = [];
  const regex = /\[zoomimg:([\s\S]*?)\]/g; // non-greedy inside

  let match;
  let clean = text;

  while ((match = regex.exec(text)) !== null) {
    const inner = match[1].trim(); // e.g. "/path|caption"
    let src = inner;
    let caption = null;

    if (inner.includes("|")) {
      const parts = inner.split("|");
      src = parts[0].trim();
      caption = (parts[1] || "").trim() || null;
    }

    if (src) {
      images.push({ src, caption });
    }
  }

  // remove all [zoomimg: ... ] tokens from the text we'll display as copy
  clean = clean.replace(regex, "").trim();

  return { clean, images };
};

const isSvg = (url) => /\.svg(\?|#|$)/i.test((url || "").trim());
// Decide if this list item renders description on the *next line*
// Rules:
// - If there's a '---', we only consider it multiline if the part *after* '---' contains a newline
// - If there's no '---', but the item itself includes a newline, also treat as multiline
const isMultilineListItem = (item) => {
  const { clean } = extractInlineZoomimgs(item);
  const dividerIndex = clean.indexOf("---");
  if (dividerIndex === -1) {
    return clean.includes("\n");
  }
  const normalPart = clean.slice(dividerIndex + 3);
  return normalPart.includes("\n");
};

export const renderContent = (
  rawContent,
  { h3Variant = "letters", h3Start = 1, listTight = true } = {}
) => {
  // 1) Normalize code fences → [code:lang|...]
  let content = normalizeCodeFences(rawContent || "");

  // 2) Normalize markdown headings → [h3:]
  content = processMarkdownHeadings(content);

  // Helper to detect plain text (no special tags/HTML)
  const isPlainText = (text) => {
    const t = text || "";
    return (
      !t.includes("<img") &&
      !t.includes("<ul") &&
      !t.includes("<ol") &&
      !t.includes("<table") &&
      !t.includes("[img:") &&
      !t.includes("[ul:") &&
      !t.includes("[ol:") &&
      !t.includes("[table:") &&
      !t.includes("[h3:") &&
      !t.includes("[quote:") &&
      !t.toLowerCase().includes("[b:") &&
      !t.includes("[link:") &&
      !t.includes("[code:") &&
      !t.includes("**")
    );
  };

  // Fast path: plain text
  if (isPlainText(content)) {
    return (
      <Typography
        variant="body1"
        sx={{
          lineHeight: 1.7,
          whiteSpace: "pre-line",
        }}
      >
        {processInlineFormatting(content)}
      </Typography>
    );
  }

  // If raw HTML tags exist, render as HTML
  if (
    content.includes("<img") ||
    content.includes("<ul") ||
    content.includes("<ol") ||
    content.includes("<table")
  ) {
    return (
      <Typography
        variant="body1"
        sx={{ lineHeight: 1.7 }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  // Parser state
  const parsedElements = [];
  let currentIndex = 0;
  let key = 0;
  let h3Counter = 0;

  // Prefix helpers for h3
  const numberToLetter = (num) => String.fromCharCode(97 + num); // a, b, c...
  const numberToNumeric = (num, start) => (start + num).toString(); // 1, 2, 3...
  const makeH3Prefix = (index, variant, start) => {
    switch ((variant || "letters").toLowerCase()) {
      case "numbers":
      case "numeric":
      case "number":
        return `${numberToNumeric(index, start)}.`;
      case "none":
        return "";
      case "letters":
      default:
        return `${numberToLetter(index)}.`;
    }
  };

  while (currentIndex < content.length) {
    // [code:language|code_content]
    if (content.indexOf("[code:", currentIndex) === currentIndex) {
      try {
        const endIndex = findBalancedClosing(content, currentIndex);
        if (endIndex > 0) {
          const codeContent = content.substring(currentIndex + 6, endIndex);
          let language = "text";
          let code = codeContent;

          if (codeContent.includes("|")) {
            const parts = codeContent.split("|");
            language = (parts[0] || "text").trim();
            code = parts.slice(1).join("|").trim();
          }

          // unescape any bracket escapes we introduced
          code = unescapeFromBracketedTag(code);

          if (code) {
            parsedElements.push(renderCodeBlock(code, language));
            key++;
          }
          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering code block:", error);
        const endIndex = findBalancedClosing(content, currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // [img:...]
    if (content.indexOf("[img:", currentIndex) === currentIndex) {
      try {
        const endIndex = findBalancedClosing(content, currentIndex);
        if (endIndex > 0) {
          const imgContent = content.substring(currentIndex + 5, endIndex);
          let imgSrc = imgContent;
          let imgCaption = null;

          if (imgContent.includes("|")) {
            const parts = imgContent.split("|");
            imgSrc = (parts[0] || "").trim();
            imgCaption = parts.length > 1 ? (parts[1] || "").trim() : null;
          }

          if (imgSrc) {
            parsedElements.push(
              <Box key={`img-${key}`} sx={{ my: 3, textAlign: "center" }}>
                <Box
                  component="img"
                  src={imgSrc}
                  alt={imgCaption || "Blog image"}
                  sx={{
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: 1,
                  }}
                  onError={(e) => {
                    console.log("Image failed to load:", imgSrc);
                    e.target.onerror = null;
                    e.target.src = "/assets/placeholder-image.jpg";
                  }}
                />
                {imgCaption && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 1,
                      color: "white",
                    }}
                  >
                    {imgCaption}
                  </Typography>
                )}
              </Box>
            );
            key++;
          }
          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering image:", error);
        const endIndex = findBalancedClosing(content, currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // [ul: ...] — safe split with nested ^^
    if (content.indexOf("[ul:", currentIndex) === currentIndex) {
      try {
        const endIndex = findBalancedClosing(content, currentIndex);
        if (endIndex > 0) {
          const listContent = content.substring(currentIndex + 4, endIndex);
          if (listContent.trim()) {
            const items = safeSplitTopLevel(listContent, "|");

            const renderItemContentForUL = (item) => {
              const { clean, images } = extractInlineZoomimgs(item);
              const dividerIndex = clean.indexOf("---");

              const makeBody = (boldPart, normalPart) => {
                const hasNewline = !!normalPart && normalPart.includes("\n");

                return (
                  <Box sx={{ width: "100%" }}>
                    <Typography variant="body1" component="div">
                      {/* Title */}
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {processInlineFormatting(boldPart)}
                      </Box>

                      {normalPart &&
                        (hasNewline ? (
                          // If user typed \n, show description on the next line and honor line breaks
                          <Box
                            component="div"
                            sx={{
                              fontWeight: 400,
                              mt: 0.5,
                              whiteSpace: "pre-line",
                            }}
                          >
                            {processInlineFormatting(normalPart)}
                          </Box>
                        ) : (
                          // Otherwise keep it inline
                          <Box
                            component="span"
                            sx={{ fontWeight: 400, ml: 0.5 }}
                          >
                            {processInlineFormatting(normalPart)}
                          </Box>
                        ))}
                    </Typography>

                    {/* Inline zoom images, if any */}
                    {images?.map((img, idx) => (
                      <Box key={idx} sx={{ mt: 1.5 }}>
                        <ZoomableImage
                          src={img.src}
                          alt={img.caption || "Blog zoomable image"}
                          caption={img.caption}
                          {...(!isSvg(img.src) ? { height: "600px" } : {})}
                          style={
                            isSvg(img.src)
                              ? { maxWidth: "100%", height: "auto" }
                              : undefined
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                );
              };

              if (dividerIndex !== -1) {
                const boldPart = clean.substring(0, dividerIndex).trim();
                // Preserve user-entered newlines after '---'
                let normalPart = clean.substring(dividerIndex + 3);
                // Normalize CRLF to \n (optional)
                normalPart = normalPart.replace(/\r\n/g, "\n");
                return makeBody(boldPart, normalPart);
              }
              return (
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body1" component="div">
                    {processInlineFormatting(clean)}
                  </Typography>
                  {images.map((img, idx) => (
                    <Box key={idx} sx={{ mt: 1.5 }}>
                      <ZoomableImage
                        src={img.src}
                        alt={img.caption || "Blog zoomable image"}
                        caption={img.caption}
                        {...(!isSvg(img.src) ? { height: "600px" } : {})}
                        style={
                          isSvg(img.src)
                            ? { maxWidth: "100%", height: "auto" }
                            : undefined
                        }
                      />
                    </Box>
                  ))}
                </Box>
              );
            };

            const renderListItem = (item, itemKey, isNested = false) => {
              // decide if this item has a description (title---desc or multiline)
              const baseClean = extractInlineZoomimgs(item).clean;
              const hasDescription =
                baseClean.includes("---") || baseClean.includes("\n");

              if (item.includes("^^")) {
                const parts = item.split("^^").map((p) => p.trim());
                const mainItem = parts[0];
                const nestedItems = parts.slice(1).filter(Boolean);

                return (
                  <ListItem
                    key={itemKey}
                    sx={{
                      display: "list-item",
                      listStyleType: isNested ? "circle" : "disc",
                      pt: 0,
                      pb: 0,
                      // add space only for items with descriptions
                      mb: listTight ? 0 : 1.5,
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    {renderItemContentForUL(mainItem)}
                    {nestedItems.length > 0 && (
                      <List
                        sx={{
                          paddingLeft: "32px !important",
                          marginLeft: "16px",
                          pt: 0,
                          pb: 0,
                          mt: 0.5,
                          width: "100%",
                          "& .MuiListItem-root": {
                            paddingLeft: "0px !important",
                          },
                        }}
                      >
                        {nestedItems.map((n, idx) =>
                          renderListItem(n, `${itemKey}-n-${idx}`, true)
                        )}
                      </List>
                    )}
                  </ListItem>
                );
              }

              return (
                <ListItem
                  key={itemKey}
                  sx={{
                    display: "list-item",
                    listStyleType: isNested ? "circle" : "disc",
                    pt: 0,
                    pb: 0,
                    // add space only for items with descriptions
                    mb: hasDescription ? 2 : 0,
                  }}
                >
                  {renderItemContentForUL(item)}
                </ListItem>
              );
            };

            parsedElements.push(
              <List
                key={`ul-${key}`}
                sx={{
                  pl: 3, // consistent left padding for markers
                  pt: 0,
                  pb: 0,
                  mt: 0,
                  listStylePosition: "outside",
                  "& .MuiListItem-root": {
                    alignItems: "flex-start",
                    p: 0,
                    mb: listTight ? 0 : 1.5, // NEW: tight everywhere except Benefits
                    "&::marker": { fontVariantNumeric: "tabular-nums" },
                  },
                }}
              >
                {items.map((item, i) => renderListItem(item, i))}
              </List>
            );
            key++;
          }
          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering unordered list:", error);
        const endIndex = findBalancedClosing(content, currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // [ol: ...] — safe split with nested ||
    if (content.indexOf("[ol:", currentIndex) === currentIndex) {
      try {
        const endIndex = findBalancedClosing(content, currentIndex);
        if (endIndex > 0) {
          const listContent = content.substring(currentIndex + 4, endIndex);
          if (listContent.trim()) {
            const items = safeSplitTopLevel(listContent, "|");

            const renderItemContentForOL = (item) => {
              const { clean, images } = extractInlineZoomimgs(item);
              const dividerIndex = clean.indexOf("---");

              const makeBody = (boldPart, normalPart) => {
                const hasNewline = !!normalPart && normalPart.includes("\n");

                return (
                  <Box sx={{ width: "100%" }}>
                    <Typography variant="body1" component="div">
                      {/* Title */}
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {processInlineFormatting(boldPart)}
                      </Box>

                      {normalPart &&
                        (hasNewline ? (
                          <Box
                            component="div"
                            sx={{
                              fontWeight: 400,
                              lineHeight: 1.6,
                            }}
                          >
                            {processInlineFormatting(normalPart)}
                          </Box>
                        ) : (
                          <Box
                            component="span"
                            sx={{ fontWeight: 400, ml: 0.5 }}
                          >
                            {processInlineFormatting(normalPart)}
                          </Box>
                        ))}
                    </Typography>

                    {/* Inline zoom images, if any */}
                    {images?.map((img, idx) => (
                      <Box key={idx} sx={{ mt: 1.5 }}>
                        <ZoomableImage
                          src={img.src}
                          alt={img.caption || "Blog zoomable image"}
                          caption={img.caption}
                          {...(!isSvg(img.src) ? { height: "600px" } : {})}
                          style={
                            isSvg(img.src)
                              ? { maxWidth: "100%", height: "auto" }
                              : undefined
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                );
              };

              if (dividerIndex !== -1) {
                const boldPart = clean.substring(0, dividerIndex).trim();
                // Preserve user-entered newlines after '---'
                let normalPart = clean.substring(dividerIndex + 3);
                // Normalize CRLF to \n (optional)
                normalPart = normalPart.replace(/\r\n/g, "\n");
                return makeBody(boldPart, normalPart);
              }
              return (
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body1" component="div">
                    {processInlineFormatting(clean)}
                  </Typography>
                  {images.map((img, idx) => (
                    <Box key={idx} sx={{ mt: 1.5 }}>
                      <ZoomableImage
                        src={img.src}
                        alt={img.caption || "Blog zoomable image"}
                        caption={img.caption}
                        {...(!isSvg(img.src) ? { height: "600px" } : {})}
                        style={
                          isSvg(img.src)
                            ? { maxWidth: "100%", height: "auto" }
                            : undefined
                        }
                      />
                    </Box>
                  ))}
                </Box>
              );
            };

            const renderListItem = (item, itemKey, isNested = false) => {
              const hasMultiline = isMultilineListItem(item);

              if (item.includes("||")) {
                const parts = item.split("||").map((p) => p.trim());
                const mainItem = parts[0];
                const nestedItems = parts.slice(1).filter(Boolean);

                return (
                  <ListItem
                    key={itemKey}
                    sx={{
                      display: "list-item",
                      listStyleType: isNested ? "lower-alpha" : "decimal",
                      mt: 0,
                      mb: listTight ? 0 : 1.5,
                    }}
                  >
                    {renderItemContentForOL(mainItem)}
                    {nestedItems.length > 0 && (
                      <List
                        sx={{
                          pl: 3, // match parent list padding
                          pt: 0,
                          pb: 0,
                          mt: 0.5,
                          "& .MuiListItem-root": {
                            p: 0,
                            "&::marker": { fontVariantNumeric: "tabular-nums" },
                          },
                        }}
                      >
                        {nestedItems.map((n, idx) =>
                          renderListItem(n, `${itemKey}-n-${idx}`, true)
                        )}
                      </List>
                    )}
                  </ListItem>
                );
              }

              return (
                <ListItem
                  key={itemKey}
                  sx={{
                    display: "list-item",
                    listStyleType: isNested ? "lower-alpha" : "decimal",
                    mt: 0,
                    mb: listTight ? 0 : 1.5,
                  }}
                >
                  {renderItemContentForOL(item)}
                </ListItem>
              );
            };

            parsedElements.push(
              <List
                key={`ol-${key}`}
                sx={{
                  pl: 3, // same as UL
                  pt: 0,
                  pb: 0,
                  mt: 0,
                  listStylePosition: "outside",
                  "& .MuiListItem-root": {
                    alignItems: "flex-start",
                    p: 0,
                    mb: listTight ? 0 : 1.5, // NEW
                    "&::marker": { fontVariantNumeric: "tabular-nums" },
                  },
                }}
              >
                {items.map((item, i) => renderListItem(item, i))}
              </List>
            );

            key++;
          }
          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering ordered list:", error);
        const endIndex = findBalancedClosing(content, currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // [quote: ...]
    if (content.indexOf("[quote:", currentIndex) === currentIndex) {
      try {
        const endIndex = findBalancedClosing(content, currentIndex);
        if (endIndex > 0) {
          const quoteContent = content.substring(currentIndex + 7, endIndex);
          if (quoteContent.trim()) {
            let quoteText = quoteContent;
            let quoteAuthor = null;

            if (quoteContent.includes("|")) {
              const parts = safeSplitTopLevel(quoteContent, "|");
              quoteText = (parts[0] || "").trim();
              quoteAuthor = parts.length > 1 ? (parts[1] || "").trim() : null;
            }

            if (quoteText) {
              parsedElements.push(
                <Box
                  key={`quote-${key}`}
                  sx={{
                    my: 3,
                    mx: { xs: 0, sm: 2, md: 4 },
                    p: { xs: 2.5, sm: 3 },
                    background:
                      "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)",
                    border: "1px solid rgba(102, 126, 234, 0.15)",
                    borderRadius: 2,
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "3px",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "0 2px 2px 0",
                    },
                  }}
                >
                  <Box sx={{ pl: 2 }}>
                    <Typography
                      component="blockquote"
                      sx={{
                        fontStyle: "italic",
                        fontSize: { xs: "1.05rem", sm: "1.15rem" },
                        lineHeight: 1.5,
                        color: "rgba(255, 255, 255, 0.95)",
                        mb: quoteAuthor ? 1.5 : 0,
                        fontWeight: 400,
                      }}
                    >
                      {quoteText}
                    </Typography>

                    {quoteAuthor && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          fontSize: { xs: "0.85rem", sm: "0.9rem" },
                          textAlign: "right",
                          fontWeight: 300,
                          "&::before": {
                            content: '"— "',
                            color: "rgba(102, 126, 234, 0.6)",
                            fontWeight: 400,
                          },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: quoteAuthor.replace(
                            /\*(.*?)\*/g,
                            '<em style="font-style: italic;">$1</em>'
                          ),
                        }}
                      />
                    )}
                  </Box>
                </Box>
              );
              key++;
            }
          }
          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering quote:", error);
        const endIndex = findBalancedClosing(content, currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // [zoomimg: ...]
    if (content.indexOf("[zoomimg:", currentIndex) === currentIndex) {
      try {
        const endIndex = findBalancedClosing(content, currentIndex);
        if (endIndex > 0) {
          const imgContent = content.substring(currentIndex + 9, endIndex);
          let imgSrc = imgContent;
          let imgCaption = null;

          if (imgContent.includes("|")) {
            const parts = imgContent.split("|");
            imgSrc = (parts[0] || "").trim();
            imgCaption = parts.length > 1 ? (parts[1] || "").trim() : null;
          }

          if (imgSrc) {
            parsedElements.push(
              <Box key={`zoomimg-${key}`} sx={{ my: 3, textAlign: "center" }}>
                <ZoomableImage
                  src={imgSrc}
                  alt={imgCaption || "Blog zoomable image"}
                  caption={imgCaption}
                  {...(!isSvg(imgSrc) ? { height: "600px" } : {})}
                  style={
                    isSvg(imgSrc)
                      ? { maxWidth: "100%", height: "auto" }
                      : undefined
                  }
                />
              </Box>
            );
            key++;
          }
          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering zoomable image:", error);
        const endIndex = findBalancedClosing(content, currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // [table: ...]
    if (content.indexOf("[table:", currentIndex) === currentIndex) {
      try {
        const endIndex = findBalancedClosing(content, currentIndex);
        if (endIndex > 0) {
          const tableContent = content.substring(currentIndex + 7, endIndex);
          if (tableContent.trim()) {
            const rows = safeSplitTopLevel(tableContent, "|");
            if (rows.length > 0) {
              const headers = safeSplitCSVTopLevel(rows[0])
                .map((h) => h.trim())
                .filter(Boolean);

              if (headers.length > 0) {
                parsedElements.push(
                  <Paper
                    key={`table-${key}`}
                    elevation={3}
                    sx={{
                      overflow: "hidden",
                      borderRadius: 2,
                      mt: 3,
                      mb: 4,
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      backdropFilter: "blur(10px)",
                      color: "white",
                    }}
                  >
                    <Box sx={{ overflowX: "auto" }}>
                      <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                          <TableRow
                            sx={{
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              "& th": {
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                py: 2,
                                color: "white",
                              },
                            }}
                          >
                            {headers.map((header, i) => (
                              <TableCell
                                key={i}
                                align={i === 0 ? "left" : "center"}
                              >
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.slice(1).map((row, rowIndex) => {
                            const cells = safeSplitCSVTopLevel(row).map((c) =>
                              c.trim()
                            );
                            while (cells.length < headers.length)
                              cells.push("");
                            return (
                              <TableRow
                                key={rowIndex}
                                sx={{
                                  "&:nth-of-type(odd)": {
                                    backgroundColor:
                                      "rgba(255, 255, 255, 0.03)",
                                  },
                                  "&:hover": {
                                    backgroundColor:
                                      "rgba(255, 255, 255, 0.08)",
                                    transition: "background-color 0.2s ease",
                                  },
                                }}
                              >
                                {cells.map((cell, cellIndex) => (
                                  <TableCell
                                    key={cellIndex}
                                    component={cellIndex === 0 ? "th" : "td"}
                                    scope={cellIndex === 0 ? "row" : undefined}
                                    align={cellIndex === 0 ? "left" : "center"}
                                    sx={{ color: "white" }}
                                  >
                                    {processInlineFormatting(cell)}
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>
                );
                key++;
              }
            }
          }
          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering table:", error);
        const endIndex = findBalancedClosing(content, currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // [h3: ...] with configurable prefix (letters/numbers/none)
    if (content.indexOf("[h3:", currentIndex) === currentIndex) {
      try {
        const endIndex = findBalancedClosing(content, currentIndex);
        if (endIndex > 0) {
          const headingText = content
            .substring(currentIndex + 4, endIndex)
            .trim();

          if (headingText) {
            const prefix = makeH3Prefix(h3Counter, h3Variant, h3Start);
            parsedElements.push(
              <Typography
                key={`h3-${key}`}
                variant="h3"
                component="h3"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "1.3rem", sm: "1.55rem" },
                  mt: 4,
                  mb: 2,
                  ml: 0,
                  color: "white",
                }}
              >
                {prefix && <>{prefix} </>}
                {headingText}
              </Typography>
            );
            key++;
            h3Counter++;
          }
          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering h3:", error);
        const endIndex = findBalancedClosing(content, currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // [audio: ...]
    if (content.indexOf("[audio:", currentIndex) === currentIndex) {
      try {
        const endIndex = findBalancedClosing(content, currentIndex);
        if (endIndex > 0) {
          const audioContent = content.substring(currentIndex + 7, endIndex);
          let src = audioContent;
          let caption = null;

          if (audioContent.includes("|")) {
            const parts = audioContent.split("|");
            src = (parts[0] || "").trim();
            caption = parts.length > 1 ? (parts[1] || "").trim() : null;
          }

          if (src) {
            parsedElements.push(
              <Box key={`audio-${key}`} sx={{ my: 3 }}>
                <audio controls preload="none" style={{ width: "100%" }}>
                  <source src={src} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              </Box>
            );
            key++;
          }
          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering audio:", error);
        const endIndex = findBalancedClosing(content, currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // [simpletable: ...] - Simple comparison table (like the image)
    if (content.indexOf("[simpletable:", currentIndex) === currentIndex) {
      try {
        const endIndex = findBalancedClosing(content, currentIndex);
        if (endIndex > 0) {
          const tableContent = content.substring(currentIndex + 13, endIndex);
          if (tableContent.trim()) {
            const sections = safeSplitTopLevel(tableContent, "|");
            if (sections.length >= 2) {
              const leftItems = safeSplitTopLevel(sections[0], "^")
                .map((item) => item.trim())
                .filter(Boolean);
              const rightItems = safeSplitTopLevel(sections[1], "^")
                .map((item) => item.trim())
                .filter(Boolean);

              if (leftItems.length > 0 && rightItems.length > 0) {
                parsedElements.push(
                  <SimpleComparisonTable
                    key={`simpletable-${key}`}
                    leftItems={leftItems}
                    rightItems={rightItems}
                  />
                );
                key++;
              }
            }
          }
          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering simple comparison table:", error);
        const endIndex = findBalancedClosing(content, currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // Plain text chunk between tags
    const nextSpecialTagIndex = findNextSpecialTagIndex(content, currentIndex);
    if (nextSpecialTagIndex > currentIndex) {
      const textContent = content.substring(currentIndex, nextSpecialTagIndex);
      if (textContent.trim()) {
        parsedElements.push(
          <Typography
            key={`text-${key}`}
            variant="body1"
            sx={{
              lineHeight: 1.7,
              whiteSpace: "pre-line",
              my: 2,
            }}
          >
            {processInlineFormatting(textContent)}
          </Typography>
        );
        key++;
      }
      currentIndex = nextSpecialTagIndex;
    } else {
      const remainingText = content.substring(currentIndex);
      if (remainingText.trim()) {
        parsedElements.push(
          <Typography
            key={`text-${key}`}
            variant="body1"
            sx={{
              lineHeight: 1.7,
              whiteSpace: "pre-line",
              my: 2,
            }}
          >
            {processInlineFormatting(remainingText)}
          </Typography>
        );
      }
      break;
    }
  }

  return <>{parsedElements}</>;
};

// Replace your current processInlineFormatting with this version
const processInlineFormatting = (text) => {
  const linkRegex = /\[link:([\s\S]*?)\|([\s\S]*?)\]/g;
  const boldRegex = /\[b:([\s\S]*?)\]/gi;
  const markdownBoldRegex = /\*\*([\s\S]*?)\*\*/g;
  const checkboxRegex = /☐/g;

  const hasLinks = text.includes("[link:");
  const hasBracketBold = text.toLowerCase().includes("[b:");
  const hasMarkdownBold = text.includes("**");
  const hasCheckbox = text.includes("☐");

  if (!hasLinks && !hasBracketBold && !hasMarkdownBold && !hasCheckbox) return text;

  // Helper to normalize labels for matching
  const normalize = (s) =>
    (s || "")
      .trim()
      .replace(/^(\s*[:\-–—])|([:\-–—]\s*)$/g, "") // trim leading/trailing punctuation like ":" or "—"
      .toLowerCase();

  // EXACT rules:
  // 1) Keep these bold (labels like "User:" and "Agent:")
  const boldAllow = new Set(["user", "agent"]);
  // These should render like an H3, not inline bold
  const treatAsH3 = new Set([
    "why it helps",
    "example conversation flow",
    "example conversation audio",
    "chat flow",
    "voice flow",
    "cost",
    "maintenance",
    "integration",
    "accuracy",
    "complexity",
    "latency & speed"
  ]);

  const matches = [];
  let m;

  while ((m = linkRegex.exec(text)) !== null) {
    matches.push({
      type: "link",
      start: m.index,
      end: m.index + m[0].length,
      url: m[1].trim(),
      text: (m[2] || m[1]).trim(),
    });
  }
  while ((m = boldRegex.exec(text)) !== null) {
    matches.push({
      type: "bold",
      start: m.index,
      end: m.index + m[0].length,
      text: (m[1] || "").trim(),
    });
  }
  while ((m = markdownBoldRegex.exec(text)) !== null) {
    matches.push({
      type: "markdownBold",
      start: m.index,
      end: m.index + m[0].length,
      text: (m[1] || "").trim(),
    });
  }
  while ((m = checkboxRegex.exec(text)) !== null) {
    matches.push({
      type: "checkbox",
      start: m.index,
      end: m.index + m[0].length,
      text: "",
    });
  }

  matches.sort((a, b) => a.start - b.start);

  // Remove overlaps
  const filtered = [];
  for (const cur of matches) {
    if (!filtered.some((ex) => cur.start < ex.end && cur.end > ex.start)) {
      filtered.push(cur);
    }
  }

  if (!filtered.length) return text;

  const parts = [];
  let last = 0;

  for (const seg of filtered) {
    if (seg.start > last) {
      parts.push({ type: "text", content: text.slice(last, seg.start) });
    }
    parts.push(seg);
    last = seg.end;
  }
  if (last < text.length)
    parts.push({ type: "text", content: text.slice(last) });

  return parts.map((seg, i) => {
    if (seg.type === "text") {
      return <React.Fragment key={i}>{seg.content}</React.Fragment>;
    }

    if (seg.type === "link") {
      return (
        <Box
          key={i}
          component="a"
          href={seg.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "primary.main",
            textDecoration: "underline",
            "&:hover": { textDecoration: "none" },
            wordBreak: "break-word",
          }}
        >
          {seg.text}
        </Box>
      );
    }

    if (seg.type === "checkbox") {
      return <InteractiveCheckbox key={i} />;
    }

    if (seg.type === "bold" || seg.type === "markdownBold") {
      const raw = seg.text;
      const norm = normalize(raw); // <-- ADD THIS

      // Render special labels as H3
      if (treatAsH3.has(norm)) {
        return (
          <Typography
            key={i}
            variant="h3"
            component="h3"
            sx={{
              fontWeight: 500,
              fontSize: { xs: "1.3rem", sm: "1.55rem" },
              mt: 4,
              mb: 2,
              ml: 0,
              color: "white",
              display: "block",
            }}
          >
            {raw}
          </Typography>
        );
      }

      // Normal bold behavior
      let weight = 600;
      if (boldAllow.has(norm)) weight = 700; // <-- now 'norm' exists
      return (
        <Box key={i} component="span" sx={{ fontWeight: weight }}>
          {raw}
        </Box>
      );
    }

    return null;
  });
};

const BlogPostDetail = () => {
  const { slug } = useParams();
  const blogPost = blogPosts.find((post) => post.id === slug) || blogPosts[0];
  const [activeSection, setActiveSection] = useState("");

  const seoData = useMemo(
    () => ({
      ...generateBlogSEO(blogPost, "https://www.beanbag.ai"),
      titleAnimation: { enabled: false },
    }),
    [blogPost]
  );

  useSEO(seoData);

  // Inject FAQ Schema if FAQs exist
  useEffect(() => {
    const faqSchema = generateFAQSchema(blogPost, "https://www.beanbag.ai");
    if (faqSchema) {
      const scriptId = "faq-schema";
      let script = document.getElementById(scriptId);

      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }

      script.textContent = JSON.stringify(faqSchema);
    }

    return () => {
      const script = document.getElementById("faq-schema");
      if (script) script.remove();
    };
  }, [blogPost]);

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = blogPost.sections.map((section) =>
        document.getElementById(section.id)
      );

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        if (sectionElements[i]) {
          const rect = sectionElements[i].getBoundingClientRect();
          if (rect.top <= 100) {
            setActiveSection(blogPost.sections[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [blogPost]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: 2, sm: 3, md: 4 },
          pb: { xs: 4, sm: 6, md: 8 },
          color: "white",
          marginTop: { xs: "60px", sm: "60px", md: "80px", lg: "100px" },
        }}
      >
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            {blogPost.title}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {blogPost.date} • {blogPost.readTime}
          </Typography>
          <Box
            component="img"
            src={blogPost.image}
            alt={blogPost.title}
            sx={{
              width: "100%",
              maxHeight: 500,
              objectFit: "cover",
              borderRadius: 2,
              mb: 4,
            }}
          />
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                position: { md: "sticky" },
                top: 20,
                zIndex: 10,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  backgroundColor: "#13171c",
                  borderRadius: 2,
                  color: "black",
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    borderBottom: "1px solid #eaeaea",
                    pb: 1,
                    color: "white",
                  }}
                >
                  Table of Contents
                </Typography>
                <List sx={{ pl: 0 }}>
                  {blogPost.sections.map((section) => (
                    <TOCItem
                      button
                      key={section.id}
                      active={activeSection === section.id ? 1 : 0}
                      onClick={() => scrollToSection(section.id)}
                      sx={{ py: 0.5 }}
                    >
                      <ListItemText
                        primary={section.title}
                        primaryTypographyProps={{
                          variant: "body2",
                          color:
                            activeSection === section.id ? "primary" : "white",
                          fontWeight: activeSection === section.id ? 600 : 400,
                        }}
                      />
                    </TOCItem>
                  ))}
                </List>
              </Paper>

              <Paper
                elevation={3}
                sx={{
                  p: 2.5,
                  backgroundColor: "transparent",
                  color: "white",
                  textAlign: "left",
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Author
                </Typography>

                <Divider
                  sx={{
                    backgroundColor: "rgba(68, 68, 68, 0.8)",
                    width: "100%",
                    my: { xs: 4, sm: 5, md: 2 },
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    alignItems: "flex-start",
                    textAlign: "left",
                  }}
                >
                  <Avatar
                    src={blogPost.author?.image || "/author-placeholder.jpg"}
                    alt={blogPost.author?.name || "Author"}
                    sx={{
                      width: 60,
                      height: 60,
                      mr: 2,
                      border: "2px solid #eaeaea",
                    }}
                  />

                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      {blogPost.author?.name || "Akash Ambade"}
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {blogPost.author?.designation || "Product Owner"}
                    </Typography>

                    {blogPost.author?.bio && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          fontStyle: "italic",
                          textAlign: "left",
                        }}
                      >
                        {blogPost.author.bio}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Grid>

          <Grid item xs={12} md={9}>
            {blogPost.sections.map((section) => {
              const isBenefits = section.id === "benefits-ai-voice-agents"; // <-- here

              return (
                <Box
                  key={section.id}
                  id={section.id}
                  sx={{ mb: 4, scrollMarginTop: "80px" }}
                >
                  <Typography
                    variant="h2"
                    component="h2"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      fontSize: { xs: "1.75rem", md: "2rem" },
                    }}
                  >
                    {section.title}
                  </Typography>

                  {/* Use numeric variant for h3 prefixes */}
                  <ContentRenderer
                    content={section.content}
                    h3Variant="numbers"
                    listTight={!isBenefits} // tight lists everywhere except Benefits
                  />

                  {section.id === "latest-models-and-performance" && (
                    <Box sx={{ mb: 4, mt: 2 }}>
                      <ModelComparison models={modelsData} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default BlogPostDetail;
